import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { CONSTANTS } from 'src/app/constants';
import { ConfirmationModalService } from 'src/app/providers/services/confirmation-modal.service';
import { MarketService } from 'src/app/providers/services/market.service';
import { TeamsService } from 'src/app/providers/services/teams.service';
import { Market } from 'src/app/storage/models/market.model';
import { Team } from 'src/app/storage/models/team.model';

const TEAM_ROLES = CONSTANTS.TEAM.ROLES;

@Component({
    selector: 'app-teams',
    templateUrl: './teams.component.html',
    styleUrls: ['./teams.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TeamsComponent implements OnInit, OnDestroy {
  selectedMarket: Market;
  selectedMarketSubscription: Subscription;

  textFilter: string = "";

  readonly pageSize = 10;
  currentPage = 1;

  teams: Team[];
  displayedTeams: Team[];

  addingTeam: boolean = false;
  teamToUpdate: Team;

  roles: Role[] = [
    { rolename: TEAM_ROLES.TECHNICIAN, label: "UI.ROLE.TECHNICIAN", icon: "heroWrenchScrewdriver" },
    { rolename: TEAM_ROLES.REMOTE_SUPERVISION, label: "UI.ROLE.SUPERVISION", icon: "heroLifebuoy" }
  ];

  public TEAM_ROLES = TEAM_ROLES;

  get paginatedTeams(): Team[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return (this.displayedTeams ?? []).slice(start, start + this.pageSize);
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil((this.displayedTeams?.length ?? 0) / this.pageSize));
  }

  constructor(
    private translateService: TranslateService,
    private teamsService: TeamsService,
    private confirmationModalService: ConfirmationModalService,
    private marketService: MarketService,
  ) { }

  async ngOnInit(): Promise<void> {
    this.selectedMarketSubscription = this.marketService.selectedMarket$.subscribe(async selectedMarket => {
      if (selectedMarket) {
        this.selectedMarket = selectedMarket;
        await this.getTeams();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.selectedMarketSubscription) { this.selectedMarketSubscription.unsubscribe(); }
  }

  async getTeams(): Promise<void> {
    this.teams = await this.teamsService.getMarketTeams(this.selectedMarket.marketname);
    if (this.teams == null ) { this.teams = []; }
    this.filterTeams();
  }

  openAddTeamModal(): void {
    this.addingTeam = true;
  }

  openUpdateTeamModal(team: Team): void {
    this.teamToUpdate = team;
  }

  filterTeams(resetPage = true): void {
    if (resetPage) { this.currentPage = 1; }
    const filteredTeams = (this.teams ?? [])
      .filter(team => {
        // List attribute to filter on
        const attributesToFilterOn = [
          team.teamMemberId,
          team.teamMemberFirstName,
          team.teamMemberName,
          team.peoplelocationname
        ];

        // Find at least one attribute that checks the criterion
        return attributesToFilterOn.find(attribute =>
          attribute.toUpperCase().includes(this.textFilter.toUpperCase())
        ) != null;
      });

    const activatedTeams = filteredTeams
                          .filter(team => !(team.deletionFlag))
                          .sort((a, b) => a.teamMemberName > b.teamMemberName ? 1 : -1);
    const deactivatedTeams = filteredTeams
                            .filter(team => team.deletionFlag)
                            .sort((a, b) => a.teamMemberName > b.teamMemberName ? 1 : -1);

    this.displayedTeams = [...activatedTeams, ...deactivatedTeams];
  }

  setPage(page: number): void {
    this.currentPage = Math.min(Math.max(1, page), this.pageCount);
  }

  async updateRole($event, team: Team, role: Role): Promise<void> {
    team.hasRole(role.rolename) ? await this.removeRole(team, role) : await this.addRole(team, role);
  }

  async addRole(team: Team, role: Role): Promise<void> {
    const isRoleAdded = await this.teamsService.addTeamRole(team.teamMemberId, role.rolename);
    if (isRoleAdded) {
      team.roles.push(role.rolename);
    } else {
      this.filterTeams(false);
    }
  }

  async removeRole(team: Team, role: Role): Promise<void> {
    const isRoleRemoved = await this.teamsService.removeTeamRole(team.teamMemberId, role.rolename);
    if (isRoleRemoved) {
      team.roles = team.roles.filter(teamRole => teamRole !== role.rolename);
    } else {
      this.filterTeams(false);
    }
  }

  async closeAddTeamModal(newTeam?: Team): Promise<void> {
    this.addingTeam = false;
    if (newTeam != null) {
      this.teams = [...this.teams, newTeam];
      this.filterTeams();
    }
  }

  async closeUpdateTeamModal(updatedTeam?: Team): Promise<void> {
    this.teamToUpdate = null;
    if (updatedTeam != null) {
      this.teams = this.teams.map(team => 
        team.teamMemberId === updatedTeam.teamMemberId ? updatedTeam : team
      );
      this.filterTeams();
    }
  }

  async toggleTeamActivation(team: Team): Promise<void> {
    const updatedTeam = new Team(team);
    updatedTeam.toggleActivation();
    const isTeamUpdated = await this.teamsService.updateTeam(this.selectedMarket.marketname, updatedTeam);

    // Set deletionFlag or reset to current value
    if (isTeamUpdated) { team.toggleActivation(); }

    this.filterTeams(false);
  }

  async removeTeam(teamToRemove: Team): Promise<void> {
    const translations = await this.translateService.get('TEAM').toPromise();

    const title = `<strong>${translations['REMOVE_TEAM']}</strong>`.replace('{TEAM_ID}', teamToRemove.teamMemberId);

    // Open confirmation modal
    this.confirmationModalService.openModal({
      title: title,
      buttons: [
        {
          text: await this.translateService.get('CONFIRM').toPromise(),
          method: async () => {
            this.confirmationModalService.closeModal();

            const team = new Team(teamToRemove);
            team.deletionFlag = true;
            const removed = await this.teamsService.updateTeam(this.selectedMarket.marketname, team);

            if (removed) {
              this.teams = this.teams.filter(team => team.teamMemberId !== teamToRemove.teamMemberId);
              this.filterTeams(false);
              this.setPage(this.currentPage);
            }
          }
        },
        {
          text: await this.translateService.get('CANCEL').toPromise(),
          method: async () => { this.confirmationModalService.closeModal(); }
        }
      ]
    });
  }

}

interface Role {
  rolename: string;
  label: string;
  icon: string;
}
